import { useState, useCallback } from 'react';
import { BarcodeScanner } from './BarcodeScanner';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useAuth } from '@/hooks/useAuth';
import { productService } from '@/services/productService';
import { movementService } from '@/services/movementService';
import { toast } from '@/hooks/useToast';
import { useConfirmDialog } from '@/components/shared/ConfirmDialog';
import type { Product } from '@/types/product.types';
import type { ZoneStockage } from '@/types/inventory.types';
import {
  X,
  Trash2,
  Plus,
  Minus,
  CheckCircle,
  Package,
  ScanBarcode,
} from 'lucide-react';

interface BatchItem {
  product: Product;
  barcode: string;
  quantite: number;
}

interface BatchScanModalProps {
  isOpen: boolean;
  onClose: () => void;
  zone?: ZoneStockage;
}

export function BatchScanModal({ isOpen, onClose, zone = 'reserve' }: BatchScanModalProps) {
  const { currentRestaurantId, profile } = useAuth();
  const [items, setItems] = useState<BatchItem[]>([]);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unknownBarcodes, setUnknownBarcodes] = useState<string[]>([]);
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const handleScan = useCallback(async (barcode: string) => {
    if (!currentRestaurantId) return;

    // Check if already in the list — update quantity
    setItems((prev) => {
      const existingIndex = prev.findIndex((item) => item.barcode === barcode);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], quantite: updated[existingIndex].quantite + 1 };
        toast({ title: updated[existingIndex].product.nom, description: `Quantité: ${updated[existingIndex].quantite}` });
        return updated;
      }
      return prev;
    });

    // If not found in items, lookup in DB (we need to check outside setState)
    const currentItems = items;
    if (currentItems.some((item) => item.barcode === barcode)) {
      return; // Already handled in setState above
    }

    try {
      const result = await productService.getProducts(currentRestaurantId);
      const product = result.data?.find((p) => p.code_barre === barcode);

      if (product) {
        setItems((prev) => {
          // Double check in case of race condition
          if (prev.some((item) => item.barcode === barcode)) {
            return prev.map((item) =>
              item.barcode === barcode ? { ...item, quantite: item.quantite + 1 } : item
            );
          }
          return [...prev, { product, barcode, quantite: 1 }];
        });
        toast({ title: 'Produit ajouté', description: product.nom });
      } else {
        setUnknownBarcodes((prev) =>
          prev.includes(barcode) ? prev : [...prev, barcode]
        );
        toast({
          title: 'Produit inconnu',
          description: `Code: ${barcode}`,
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        title: 'Erreur',
        description: 'Impossible de rechercher le produit',
        variant: 'destructive',
      });
    }
  }, [currentRestaurantId, items]);

  const updateQuantity = (index: number, delta: number) => {
    setItems((prev) => {
      const updated = [...prev];
      const newQty = updated[index].quantite + delta;
      if (newQty <= 0) {
        return updated.filter((_, i) => i !== index);
      }
      updated[index] = { ...updated[index], quantite: newQty };
      return updated;
    });
  };

  const removeItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleValidate = async () => {
    if (!currentRestaurantId || !profile?.id || items.length === 0) return;

    setIsSubmitting(true);

    try {
      let successCount = 0;

      for (const item of items) {
        const result = await movementService.recordEntry(
          currentRestaurantId,
          item.product.id,
          item.quantite,
          item.product.unite,
          zone,
          profile.id,
          'scan',
          'Scan batch'
        );

        if (result.success) {
          successCount++;
        }
      }

      toast({
        title: 'Scan batch terminé',
        description: `${successCount}/${items.length} entrées enregistrées`,
      });

      setItems([]);
      setUnknownBarcodes([]);
      onClose();
    } catch {
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la validation du batch',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = async () => {
    if (items.length > 0) {
      const confirmed = await confirm({
        title: 'Abandonner le scan batch ?',
        message: 'Les produits scannés seront perdus.',
        confirmLabel: 'Abandonner',
        variant: 'warning',
      });
      if (!confirmed) {
        return;
      }
    }
    setItems([]);
    setUnknownBarcodes([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Scanner overlay */}
      <BarcodeScanner
        isOpen={isScannerOpen}
        onScan={(barcode) => {
          handleScan(barcode);
          // Don't close scanner - re-open for next scan
        }}
        onClose={() => setIsScannerOpen(false)}
      />

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ScanBarcode size={20} />
            Scan batch
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {items.length} produit{items.length > 1 ? 's' : ''} • {items.reduce((s, i) => s + i.quantite, 0)} unités
          </p>
        </div>
        <button onClick={handleClose} className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
          <X size={24} />
        </button>
      </div>

      {/* Scan button */}
      <div className="p-4 flex-shrink-0">
        <Button onClick={() => setIsScannerOpen(true)} className="w-full" size="lg">
          <ScanBarcode size={20} className="mr-2" />
          Scanner un produit
        </Button>
      </div>

      {/* Items list */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
        {items.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Package size={40} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm">Scannez des produits pour les ajouter au batch</p>
            <p className="text-xs mt-1">Chaque scan ajoute 1 unité. Re-scannez pour incrémenter.</p>
          </div>
        ) : (
          items.map((item, index) => (
            <div
              key={item.barcode}
              className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-3 flex items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                  {item.product.nom}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {item.product.categorie} • {item.product.unite}
                </p>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => updateQuantity(index, -1)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  <Minus size={14} />
                </button>
                <span className="w-10 text-center font-semibold text-gray-900 dark:text-white">
                  {item.quantite}
                </span>
                <button
                  onClick={() => updateQuantity(index, 1)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  <Plus size={14} />
                </button>
              </div>

              <button
                onClick={() => removeItem(index)}
                className="p-1.5 text-red-500 hover:text-red-700 flex-shrink-0"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))
        )}

        {/* Unknown barcodes */}
        {unknownBarcodes.length > 0 && (
          <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
            <p className="text-xs font-medium text-yellow-700 dark:text-yellow-300 mb-1">
              Codes non reconnus ({unknownBarcodes.length})
            </p>
            <div className="flex flex-wrap gap-1">
              {unknownBarcodes.map((code) => (
                <Badge key={code} variant="warning" size="sm">{code}</Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer - Validate */}
      {items.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border-t dark:border-gray-700 p-4 flex-shrink-0">
          <Button
            onClick={handleValidate}
            disabled={isSubmitting}
            className="w-full"
            size="lg"
          >
            <CheckCircle size={18} className="mr-2" />
            {isSubmitting
              ? 'Enregistrement...'
              : `Valider ${items.length} produit${items.length > 1 ? 's' : ''} (${items.reduce((s, i) => s + i.quantite, 0)} unités)`
            }
          </Button>
        </div>
      )}
      {ConfirmDialogComponent}
    </div>
  );
}
